import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, ChevronLeft, ChevronRight, CircleCheck, Clock3, Download, FileUp, MessageCircle, Minus, Plus, RotateCcw, X } from "lucide-react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  acceptQuickOrder,
  closeQuickOrder,
  confirmQuickOrderProvider,
  createOrderPayment,
  createTradeMediaDownloadUrl,
  getOrderPaymentSummary,
  getPaymentRedirectUrl,
  getQuickOrder,
  listQuickOrderDeliveries,
  requestQuickOrderRevision,
  submitQuickOrderDelivery,
  uploadQuickOrderDeliveryFile,
} from "../../entities/order/api";
import { rememberPaymentOrder } from "../../entities/order/paymentReturn";
import { useAuthStore } from "../../features/auth/store";
import { IS_API_MODE } from "../../shared/config/runtime";
import { getApiErrorMessage } from "../../shared/api/errors";
import { showToast } from "../../shared/ui/toast";

const statusLabels = {
  PENDING_AUDIT: "审核中",
  WAITING_PAYMENT: "待支付",
  OPEN_FOR_GRAB: "待接单",
  PROVIDER_PENDING_CONFIRM: "待确认",
  IN_SERVICE: "服务中",
  DELIVERED: "待验收",
  IN_REVISION: "修改中",
  IN_DISPUTE: "争议中",
  COMPLETED: "已完成",
  CLOSED: "已关闭",
};

const settlementStatusLabels = {
  PENDING: "待结算",
  HELD: "已冻结",
  PROCESSING: "结算处理中",
  SUCCEEDED: "结算成功",
  FAILED_RETRYABLE: "结算待重试",
  FAILED_FINAL: "结算失败",
  CANCELLED: "已取消",
};

const INITIAL_DELIVERY_FORM = {
  versionLabel: "v1",
  summary: "",
};

const INITIAL_REVIEW_FORM = {
  acceptanceNote: "",
  revisionReason: "",
  revisionItems: "",
};

const INITIAL_CLOSE_FORM = {
  reason: "双方协商结束",
  refundAmount: "",
};

function getDeliverySnapshotKey(orderId) {
  return `tupi:quick-order-delivery-snapshot:${orderId}`;
}

function getOrderCounterparty(order, currentUserId) {
  const demandUserId = order.demand_user_id || order.demandUserId;
  const providerUserId = order.provider_user_id || order.providerUserId;
  const currentId = currentUserId ? String(currentUserId) : "";

  if (currentId && demandUserId && currentId === String(demandUserId)) {
    return providerUserId
      ? { userId: String(providerUserId), roleLabel: "服务者", buttonLabel: "联系服务者" }
      : { userId: "", roleLabel: "服务者", buttonLabel: "待服务者接单" };
  }
  if (currentId && providerUserId && currentId === String(providerUserId)) {
    return demandUserId
      ? { userId: String(demandUserId), roleLabel: "需求方", buttonLabel: "联系需求方" }
      : null;
  }
  return null;
}

function readDeliverySnapshot(orderId) {
  if (!orderId) return null;
  try {
    const raw = window.localStorage.getItem(getDeliverySnapshotKey(orderId));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeDeliverySnapshot(orderId, snapshot) {
  try {
    window.localStorage.setItem(getDeliverySnapshotKey(orderId), JSON.stringify(snapshot));
  } catch {
    // 本地存储不可用时不影响交付提交主流程。
  }
}

function formatDetailDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("zh-CN", {
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatMoney(value) {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return value || "0.00";
  return amount.toFixed(2);
}

function normalizeSettlement(settlement) {
  if (!settlement) return null;
  return {
    id: settlement.id || "",
    settlementNo: settlement.settlement_no || settlement.settlementNo || "",
    status: settlement.status || "",
    currency: settlement.currency || "CNY",
    grossAmount: settlement.gross_amount || settlement.grossAmount || "0.00",
    providerAmount: settlement.provider_amount || settlement.providerAmount || "0.00",
    platformFeeAmount: settlement.platform_fee_amount || settlement.platformFeeAmount || "0.00",
    refundAdjustmentAmount: settlement.refund_adjustment_amount || settlement.refundAdjustmentAmount || "0.00",
    availableAt: settlement.available_at || settlement.availableAt || "",
    settledAt: settlement.settled_at || settlement.settledAt || "",
    externalSettlementId: settlement.external_settlement_id || settlement.externalSettlementId || "",
    failureCode: settlement.failure_code || settlement.failureCode || "",
    channel: settlement.channel || "",
    accountMasked: settlement.account_masked || settlement.accountMasked || "",
  };
}

function getSettlementStatusCopy(status) {
  return settlementStatusLabels[status] || status || "结算处理中";
}

function getSettlementDescription(settlement, acceptanceSnapshot) {
  const status = settlement?.status || acceptanceSnapshot?.settlementStatus || "";
  if (status === "SUCCEEDED") {
    return settlement?.settledAt
      ? `结算已完成，到账时间：${formatDetailDate(settlement.settledAt)}`
      : "结算已完成，服务者已进入可结算状态。";
  }
  if (status === "HELD") {
    return "结算已创建但当前处于冻结状态，通常需要等待平台或支付通道进一步处理。";
  }
  if (status === "FAILED_RETRYABLE") {
    return "结算提交失败，但系统仍可继续重试，请稍后刷新查看最新状态。";
  }
  if (status === "FAILED_FINAL") {
    return "结算已进入最终失败状态，建议联系平台支持介入处理。";
  }
  if (status === "PENDING" || status === "PROCESSING") {
    return "结算已创建，平台正在处理打款流程，可稍后刷新查看更新。";
  }
  if (acceptanceSnapshot?.settlementId) {
    return "验收已完成，结算单已创建，详情正在同步中。";
  }
  return "验收已完成，结算信息会在创建后展示在这里。";
}

function normalizeDeliveryFile(file, index) {
  const asset = file?.asset || {};
  const assetId = file?.asset_id || file?.assetId || asset.id || "";
  const relationId = file?.id || file?.delivery_file_id || file?.deliveryFileId || "";
  const fileId = relationId || assetId || file;
  const name = file?.original_filename
    || file?.originalFilename
    || file?.filename
    || file?.file_name
    || file?.fileName
    || asset.original_filename
    || asset.originalFilename
    || `交付文件 ${index + 1}`;
  const url = file?.url
    || file?.file_url
    || file?.fileUrl
    || file?.download_url
    || file?.downloadUrl
    || asset.url
    || asset.file_url
    || asset.fileUrl
    || "";
  const thumbnailUrl = file?.thumbnail_url
    || file?.thumbnailUrl
    || asset.thumbnail_url
    || asset.thumbnailUrl
    || "";
  const mimeType = file?.mime_type || file?.mimeType || asset.mime_type || asset.mimeType || "";
  const mediaType = file?.media_type || file?.mediaType || asset.media_type || asset.mediaType || "";
  const fileType = file?.file_type || file?.fileType || asset.file_type || asset.fileType || "";
  return {
    id: String(fileId || `${name}-${index}`),
    assetId: String(assetId || ""),
    relationId: String(relationId || ""),
    name: String(name),
    url,
    thumbnailUrl,
    mimeType,
    mediaType,
    fileType,
  };
}

function getFileExtension(name = "") {
  const cleanName = String(name).split("?")[0].split("#")[0];
  const extension = cleanName.includes(".") ? cleanName.split(".").pop() : "";
  return extension.toLowerCase();
}

function isImageDeliveryFile(file) {
  const extension = getFileExtension(file.name || file.url);
  return String(file.mimeType).startsWith("image/")
    || String(file.mediaType).toUpperCase() === "IMAGE"
    || ["jpg", "jpeg", "png", "webp", "gif", "svg", "avif"].includes(extension);
}

function isVideoDeliveryFile(file) {
  const extension = getFileExtension(file.name || file.url);
  return String(file.mimeType).startsWith("video/")
    || String(file.mediaType).toUpperCase() === "VIDEO"
    || ["mp4", "webm", "mov", "m4v", "ogg"].includes(extension);
}

function getTradeMediaAccessUrl(payload) {
  return payload?.url
    || payload?.download_url
    || payload?.downloadUrl
    || payload?.signed_url
    || payload?.signedUrl
    || "";
}

async function downloadUrlToLocalFile(url, filename) {
  const response = await fetch(url);
  if (!response.ok) throw new Error("交付文件下载失败");
  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = filename || "delivery-file";
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
}

function DeliveryImageViewer({
  items,
  activeIndex,
  zoom,
  offset,
  dragging,
  onClose,
  onPrevious,
  onNext,
  onZoom,
  onReset,
  onPointerDown,
  onPointerMove,
  onPointerEnd,
}) {
  const activeImage = items[activeIndex] || null;
  const hasMultipleImages = items.length > 1;
  const accessUrlQuery = useQuery({
    queryKey: ["trade-media", "download-url", "viewer", activeImage?.assetId || activeImage?.id || ""],
    queryFn: () => createTradeMediaDownloadUrl(activeImage.assetId),
    enabled: Boolean(activeImage?.assetId),
    staleTime: 4 * 60 * 1000,
    retry: 1,
  });
  const signedUrl = getTradeMediaAccessUrl(accessUrlQuery.data);
  const imageUrl = signedUrl || (!activeImage?.assetId ? activeImage?.url : "");

  if (!activeImage) return null;

  return (
    <div
      className="detail-media-viewer delivery-image-viewer"
      role="dialog"
      aria-modal="true"
      aria-label={`放大查看交付图片，第 ${activeIndex + 1} 张，共 ${items.length} 张`}
      onWheel={(event) => {
        event.preventDefault();
        onZoom(event.deltaY < 0 ? 0.2 : -0.2);
      }}
      onClick={onClose}
    >
      <button
        className="detail-media-viewer-close"
        type="button"
        aria-label="关闭图片预览"
        onClick={onClose}
      >
        <X size={27} />
      </button>
      {hasMultipleImages ? (
        <>
          <button
            className="detail-media-viewer-control previous"
            type="button"
            aria-label="上一张图片"
            onClick={(event) => {
              event.stopPropagation();
              onPrevious();
            }}
          >
            <ChevronLeft size={30} />
          </button>
          <button
            className="detail-media-viewer-control next"
            type="button"
            aria-label="下一张图片"
            onClick={(event) => {
              event.stopPropagation();
              onNext();
            }}
          >
            <ChevronRight size={30} />
          </button>
          <span className="detail-media-viewer-count">
            {activeIndex + 1}/{items.length}
          </span>
        </>
      ) : null}
      <div
        className="detail-media-viewer-toolbar"
        aria-label="图片缩放控制"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          aria-label="缩小图片"
          disabled={zoom <= 0.5}
          onClick={() => onZoom(-0.25)}
        >
          <Minus size={20} />
        </button>
        <button
          className="detail-media-zoom-value"
          type="button"
          aria-label="恢复图片适应屏幕"
          onClick={onReset}
        >
          {Math.round(zoom * 100)}%
        </button>
        <button
          type="button"
          aria-label="放大图片"
          disabled={zoom >= 4}
          onClick={() => onZoom(0.25)}
        >
          <Plus size={20} />
        </button>
        <button
          type="button"
          aria-label="重置图片位置和比例"
          onClick={onReset}
        >
          <RotateCcw size={18} />
        </button>
      </div>
      {accessUrlQuery.isError ? <div className="detail-state error">图片访问地址生成失败</div> : null}
      {imageUrl ? (
        <img
          className={dragging ? "dragging" : ""}
          src={imageUrl}
          alt={activeImage.name}
          draggable={false}
          style={{
            transform: `translate3d(${offset.x}px, ${offset.y}px, 0) scale(${zoom})`,
          }}
          onClick={(event) => event.stopPropagation()}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerEnd}
          onPointerCancel={onPointerEnd}
        />
      ) : accessUrlQuery.isPending ? (
        <div className="detail-state">正在加载交付图片……</div>
      ) : (
        <div className="detail-state error">暂无可访问的图片地址</div>
      )}
    </div>
  );
}

function DeliveryFilePreview({ file, onOpenImage }) {
  const [isDownloading, setIsDownloading] = useState(false);
  const accessUrlQuery = useQuery({
    queryKey: ["trade-media", "download-url", file.assetId],
    queryFn: () => createTradeMediaDownloadUrl(file.assetId),
    enabled: Boolean(file.assetId),
    staleTime: 4 * 60 * 1000,
    retry: 1,
  });
  const signedUrl = getTradeMediaAccessUrl(accessUrlQuery.data);
  const accessUrl = signedUrl || (!file.assetId ? file.url : "");
  const previewUrl = signedUrl || (!file.assetId ? file.thumbnailUrl || file.url : "");
  const canPreviewImage = Boolean(accessUrl) && isImageDeliveryFile(file);
  const canPreviewVideo = Boolean(accessUrl) && isVideoDeliveryFile(file);

  async function getFreshAccessUrl() {
    if (accessUrl) return accessUrl;
    if (!file.assetId) return "";
    const result = await accessUrlQuery.refetch();
    return getTradeMediaAccessUrl(result.data);
  }

  async function handleDownload() {
    try {
      setIsDownloading(true);
      const downloadUrl = await getFreshAccessUrl();
      if (!downloadUrl) throw new Error("暂无下载地址");
      await downloadUrlToLocalFile(downloadUrl, file.name);
    } catch (error) {
      showToast(error.message || "交付文件下载失败", "error");
    } finally {
      setIsDownloading(false);
    }
  }

  return (
    <article className="delivery-artifact-card">
      {canPreviewImage ? (
        <button
          className="delivery-artifact-preview image"
          type="button"
          onClick={() => onOpenImage(file.id)}
        >
          <img src={previewUrl} alt={file.name} />
        </button>
      ) : null}
      {canPreviewVideo ? (
        <div className="delivery-artifact-preview video">
          <video controls preload="metadata" poster={file.thumbnailUrl || undefined}>
            <source src={accessUrl} type={file.mimeType || undefined} />
            当前浏览器不支持预览该视频。
          </video>
        </div>
      ) : null}
      {!canPreviewImage && !canPreviewVideo ? (
        <div className="delivery-artifact-preview file">
          <Download size={24} />
          <span>{file.fileType || getFileExtension(file.name) || "FILE"}</span>
        </div>
      ) : null}
      <footer>
        <span title={file.name}>{file.name}</span>
        <div>
          {accessUrlQuery.isPending ? <small>正在生成访问地址…</small> : null}
          {accessUrlQuery.isError ? <small>访问地址生成失败</small> : null}
          {accessUrl ? (
            <>
              {canPreviewImage ? (
                <button type="button" onClick={() => onOpenImage(file.id)}>查看</button>
              ) : (
                <a href={accessUrl} target="_blank" rel="noreferrer">查看</a>
              )}
              <button type="button" disabled={isDownloading} onClick={handleDownload}>
                {isDownloading ? "下载中…" : "下载"}
              </button>
            </>
          ) : !accessUrlQuery.isPending && !accessUrlQuery.isError ? (
            <small>暂无下载地址</small>
          ) : null}
        </div>
      </footer>
    </article>
  );
}

function normalizeDeliverySubmission(order, localSnapshot) {
  const deliveryList = order.delivery_submissions || order.deliverySubmissions || order.deliveries || [];
  const latestFromList = Array.isArray(deliveryList) && deliveryList.length
    ? deliveryList[deliveryList.length - 1]
    : null;
  const delivery = order.latest_delivery
    || order.latestDelivery
    || order.delivery_submission
    || order.deliverySubmission
    || order.delivery
    || latestFromList
    || localSnapshot;

  if (!delivery) return null;

  const rawFiles = delivery.files
    || delivery.file_assets
    || delivery.fileAssets
    || delivery.assets
    || delivery.file_list_json
    || delivery.fileListJson
    || [];
  const files = Array.isArray(rawFiles)
    ? rawFiles.map((file, index) => normalizeDeliveryFile(file, index))
    : [];

  return {
    versionLabel: delivery.version_label || delivery.versionLabel || "未标记版本",
    summary: delivery.summary || "",
    submittedAt: delivery.submitted_at || delivery.submittedAt || order.delivered_at || order.deliveredAt || "",
    files,
    isLocalOnly: Boolean(delivery.isLocalOnly),
  };
}

function getDeliveryItems(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.results)) return payload.results;
  return [];
}

function isMethodNotAllowed(error) {
  const detail = error?.response?.data?.detail || error?.response?.data;
  return Number(error?.response?.status) === 405
    || /method .*not allowed|方法.*不被允许/i.test(String(detail || ""));
}

function getDeliveryErrorMessage(error) {
  if (!error?.response && error?.message) return error.message;
  return getApiErrorMessage(error, "交付提交失败");
}

function getDeliveryReviewErrorMessage(error) {
  if (!error?.response && error?.message) return error.message;
  return getApiErrorMessage(error, "验收操作失败");
}

function parseRevisionItems(value) {
  return String(value)
    .split("\n")
    .map((item) => item.trim())
    .filter(Boolean);
}

export default function OrderDetailPage() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const currentUserId = useAuthStore((state) => state.user?.id);
  const [deliveryForm, setDeliveryForm] = useState(INITIAL_DELIVERY_FORM);
  const [reviewForm, setReviewForm] = useState(INITIAL_REVIEW_FORM);
  const [closeForm, setCloseForm] = useState(INITIAL_CLOSE_FORM);
  const [closePanelOpen, setClosePanelOpen] = useState(false);
  const [closeValidationError, setCloseValidationError] = useState("");
  const [acceptanceSnapshot, setAcceptanceSnapshot] = useState(null);
  const [deliveryFiles, setDeliveryFiles] = useState([]);
  const [deliveryProgress, setDeliveryProgress] = useState(0);
  const [localDeliverySnapshot, setLocalDeliverySnapshot] = useState(() => readDeliverySnapshot(orderId));
  const [deliveryImageViewerIndex, setDeliveryImageViewerIndex] = useState(-1);
  const [deliveryImageZoom, setDeliveryImageZoom] = useState(1);
  const [deliveryImageOffset, setDeliveryImageOffset] = useState({ x: 0, y: 0 });
  const [deliveryImageDragging, setDeliveryImageDragging] = useState(false);
  const deliveryImageDragRef = useRef(null);
  const returnedFromPayment = searchParams.get("payment") === "return";
  const orderQuery = useQuery({
    queryKey: ["orders", orderId],
    queryFn: () => getQuickOrder(orderId),
    enabled: IS_API_MODE && Boolean(orderId),
    refetchInterval: (query) => {
      const order = query.state.data?.order || query.state.data;
      if (!returnedFromPayment || !order) return false;
      return order.order_status === "WAITING_PAYMENT" || order.pay_status === "PENDING"
        ? 3_000
        : false;
    },
  });
  const queriedOrder = orderQuery.data?.order || orderQuery.data;
  const shouldLoadDeliveries = ["DELIVERED", "COMPLETED", "IN_REVISION", "IN_DISPUTE"].includes(
    queriedOrder?.order_status,
  );
  const deliveriesQuery = useQuery({
    queryKey: ["orders", orderId, "deliveries"],
    queryFn: () => listQuickOrderDeliveries(orderId),
    enabled: IS_API_MODE && Boolean(orderId) && shouldLoadDeliveries,
    retry: (failureCount, error) => !isMethodNotAllowed(error) && failureCount < 2,
  });
  const actionMutation = useMutation({
    mutationFn: ({ action }) => {
      if (action === "pay") {
        const payStatus = orderQuery.data?.order?.pay_status || orderQuery.data?.pay_status;
        if (payStatus === "PENDING") {
          return getOrderPaymentSummary(orderId).then((summary) => (
            getPaymentRedirectUrl(summary)
              ? summary
              : createOrderPayment(orderId, { channel: "ALIPAY" })
          ));
        }
        return createOrderPayment(orderId, { channel: "ALIPAY" });
      }
      if (action === "accept") return acceptQuickOrder(orderId);
      if (action === "provider-confirm") {
        return confirmQuickOrderProvider(orderId, {
          confirm: true,
          note: "服务者确认接单",
        });
      }
      if (action === "provider-reject") {
        return confirmQuickOrderProvider(orderId, {
          confirm: false,
          note: "服务者放弃接单",
        });
      }
      return closeQuickOrder(orderId, {
        closure_reason: closeForm.reason.trim() || "双方协商结束",
        ...(closeForm.refundAmount.trim()
          ? { refund_amount_yuan: closeForm.refundAmount.trim() }
          : {}),
      });
    },
    onSuccess: (response, variables) => {
      queryClient.invalidateQueries({ queryKey: ["orders", orderId] });
      queryClient.invalidateQueries({ queryKey: ["orders", "mine", "provider"] });
      queryClient.invalidateQueries({ queryKey: ["orders", "mine", "customer"] });
      queryClient.invalidateQueries({ queryKey: ["orders", orderId, "deliveries"] });
      if (variables.action === "provider-confirm") {
        showToast("已确认接单，订单进入服务中");
        return;
      }
      if (variables.action === "provider-reject") {
        showToast("已放弃接单，订单将重新开放给其他服务者");
        return;
      }
      if (variables.action === "close") {
        setClosePanelOpen(false);
        setCloseValidationError("");
        setCloseForm(INITIAL_CLOSE_FORM);
        showToast("订单已关闭");
        return;
      }
      if (variables.action !== "pay") return;

      const redirectUrl = getPaymentRedirectUrl(response);
      if (redirectUrl) {
        rememberPaymentOrder(response, orderId);
        window.location.assign(redirectUrl);
        return;
      }

      // Manual payment adapters intentionally do not return a browser URL.
      // Keep the user informed instead of silently leaving the page unchanged.
      showToast("支付请求已创建，请按页面提示完成支付");
    },
  });
  const deliveryMutation = useMutation({
    mutationFn: async () => {
      if (!deliveryForm.versionLabel.trim()) {
        throw new Error("请填写交付版本号");
      }
      if (!deliveryFiles.length) {
        throw new Error("请至少上传一份交付文件");
      }

      setDeliveryProgress(0);
      const assets = [];
      for (let index = 0; index < deliveryFiles.length; index += 1) {
        const deliveryFile = deliveryFiles[index];
        const asset = await uploadQuickOrderDeliveryFile(deliveryFile.file, (fileProgress) => {
          setDeliveryProgress(Math.round(
            ((index + fileProgress / 100) / deliveryFiles.length) * 100,
          ));
        });
        assets.push(asset);
      }
      const payload = {
        version_label: deliveryForm.versionLabel.trim(),
        summary: deliveryForm.summary.trim(),
        file_asset_ids: assets.map((asset) => String(asset.id)),
      };
      return submitQuickOrderDelivery(orderId, payload).then((result) => ({
        result,
        submittedDelivery: {
          versionLabel: payload.version_label,
          summary: payload.summary,
          submittedAt: new Date().toISOString(),
          files: assets.map((asset, index) => ({
            id: String(asset.id || payload.file_asset_ids[index]),
            assetId: String(asset.id || payload.file_asset_ids[index]),
            name: asset.original_filename || asset.originalFilename || deliveryFiles[index]?.file.name || `交付文件 ${index + 1}`,
            url: asset.url || asset.file_url || asset.fileUrl || "",
            thumbnailUrl: asset.thumbnail_url || asset.thumbnailUrl || "",
            mimeType: asset.mime_type || asset.mimeType || deliveryFiles[index]?.file.type || "",
            mediaType: asset.media_type || asset.mediaType || "",
            fileType: asset.file_type || asset.fileType || "",
          })),
          isLocalOnly: true,
        },
      }));
    },
    onSuccess: ({ submittedDelivery }) => {
      queryClient.invalidateQueries({ queryKey: ["orders", orderId] });
      queryClient.invalidateQueries({ queryKey: ["orders", "mine", "provider"] });
      queryClient.invalidateQueries({ queryKey: ["orders", "mine", "customer"] });
      queryClient.invalidateQueries({ queryKey: ["orders", orderId, "deliveries"] });
      writeDeliverySnapshot(orderId, submittedDelivery);
      setLocalDeliverySnapshot(submittedDelivery);
      setDeliveryFiles([]);
      setDeliveryProgress(0);
      showToast("交付已提交，等待需求方验收");
    },
    onError: (error) => {
      showToast(getDeliveryErrorMessage(error), "error");
    },
  });
  const reviewMutation = useMutation({
    mutationFn: async ({ action: reviewAction }) => {
      if (reviewAction === "accept") {
        return {
          action: reviewAction,
          response: await acceptQuickOrder(
            orderId,
            reviewForm.acceptanceNote.trim() || "确认交付物符合验收标准",
          ),
        };
      }

      if (!reviewForm.revisionReason.trim()) {
        throw new Error("请填写退回修改原因");
      }

      return {
        action: reviewAction,
        response: await requestQuickOrderRevision(orderId, {
          reason: reviewForm.revisionReason.trim(),
          request_items_json: parseRevisionItems(reviewForm.revisionItems),
        }),
      };
    },
    onSuccess: ({ action: reviewAction, response }) => {
      queryClient.invalidateQueries({ queryKey: ["orders", orderId] });
      queryClient.invalidateQueries({ queryKey: ["orders", "mine", "provider"] });
      queryClient.invalidateQueries({ queryKey: ["orders", "mine", "customer"] });
      queryClient.invalidateQueries({ queryKey: ["orders", orderId, "deliveries"] });
      setReviewForm(INITIAL_REVIEW_FORM);
      if (reviewAction === "accept") {
        setAcceptanceSnapshot({
          acceptanceId: response?.acceptance_id || response?.acceptanceId || "",
          settlementId: response?.settlement_id || response?.settlementId || "",
          settlementStatus: response?.settlement_status || response?.settlementStatus || "",
        });
        const settlementStatus = response?.settlement_status || response?.settlementStatus || "";
        showToast(
          settlementStatus
            ? `已确认验收，${getSettlementStatusCopy(settlementStatus)}`
            : "已确认验收，订单进入已完成",
        );
        return;
      }
      setAcceptanceSnapshot(null);
      showToast("已退回修改，等待服务者重新交付");
    },
    onError: (error) => {
      showToast(getDeliveryReviewErrorMessage(error), "error");
    },
  });

  const order = queriedOrder || {
    order_no: orderId,
    title: "演示订单",
    order_status: "IN_SERVICE",
    amount_yuan: "0.00",
    requirement_desc: "演示模式订单详情",
  };
  const status = statusLabels[order.order_status] || order.order_status;
  const isProviderPendingConfirm = order.order_status === "PROVIDER_PENDING_CONFIRM";
  const counterparty = getOrderCounterparty(order, currentUserId);
  const isDemandUser = Boolean(
    currentUserId
      && (order.demand_user_id || order.demandUserId)
      && String(currentUserId) === String(order.demand_user_id || order.demandUserId),
  );
  const deliveryItems = getDeliveryItems(deliveriesQuery.data);
  const latestDelivery = normalizeDeliverySubmission(order, deliveryItems[0] || localDeliverySnapshot);
  const deliveryImageItems = (latestDelivery?.files || []).filter((file) => isImageDeliveryFile(file));
  const settlementSummary = normalizeSettlement(orderQuery.data?.settlement || queriedOrder?.settlement || null);
  const deliveriesMethodNotAllowed = deliveriesQuery.isError && isMethodNotAllowed(deliveriesQuery.error);
  const isAssignedProvider = Boolean(
    currentUserId
      && (order.provider_user_id || order.providerUserId)
      && String(currentUserId) === String(order.provider_user_id || order.providerUserId),
  );
  const canSubmitDelivery = isAssignedProvider && ["IN_SERVICE", "IN_REVISION"].includes(order.order_status);
  const canReviewDelivery = isDemandUser && order.order_status === "DELIVERED";
  const canRequestRevision = canReviewDelivery && Number(order.remaining_revision_rounds ?? 0) > 0;
  const requiresRefundAmountOnClose = ["PAID", "PARTIALLY_REFUNDED"].includes(String(order.pay_status || ""));
  const action = order.order_status === "WAITING_PAYMENT"
    ? { key: "pay", label: order.pay_status === "PENDING" ? "继续支付" : "立即支付" }
    : !isProviderPendingConfirm && !["DELIVERED", "COMPLETED", "CLOSED"].includes(order.order_status)
        ? { key: "close", label: "关闭订单" }
        : null;
  const secondaryAction = action?.key === "pay" && order.pay_status !== "PENDING"
    ? { key: "close", label: "关闭订单" }
    : null;
  const deliveryError = deliveryMutation.isError
    ? getDeliveryErrorMessage(deliveryMutation.error)
    : "";
  const reviewError = reviewMutation.isError
    ? getDeliveryReviewErrorMessage(reviewMutation.error)
    : "";
  const closeError = closeValidationError
    || (
      closePanelOpen
      && actionMutation.isError
      && actionMutation.variables?.action === "close"
        ? getApiErrorMessage(actionMutation.error, "关闭订单失败")
        : ""
    );

  useEffect(() => {
    if (deliveryImageViewerIndex < 0) return undefined;
    const closeOnEscape = (event) => {
      if (event.key === "Escape") {
        setDeliveryImageViewerIndex(-1);
      } else if (event.key === "ArrowLeft") {
        resetDeliveryImageTransform();
        setDeliveryImageViewerIndex((current) => (
          deliveryImageItems.length
            ? (current - 1 + deliveryImageItems.length) % deliveryImageItems.length
            : current
        ));
      } else if (event.key === "ArrowRight") {
        resetDeliveryImageTransform();
        setDeliveryImageViewerIndex((current) => (
          deliveryImageItems.length
            ? (current + 1) % deliveryImageItems.length
            : current
        ));
      }
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [deliveryImageItems.length, deliveryImageViewerIndex]);

  if (IS_API_MODE && orderQuery.isPending) return <div className="detail-state">正在加载订单详情……</div>;
  if (IS_API_MODE && orderQuery.isError) {
    return <div className="detail-state error">{getApiErrorMessage(orderQuery.error, "订单详情加载失败")}</div>;
  }

  function resetDeliveryImageTransform() {
    setDeliveryImageZoom(1);
    setDeliveryImageOffset({ x: 0, y: 0 });
    setDeliveryImageDragging(false);
    deliveryImageDragRef.current = null;
  }

  function openDeliveryImageViewer(fileId) {
    const nextIndex = deliveryImageItems.findIndex((file) => file.id === fileId);
    if (nextIndex < 0) return;
    resetDeliveryImageTransform();
    setDeliveryImageViewerIndex(nextIndex);
  }

  function changeDeliveryImageZoom(change) {
    setDeliveryImageZoom((current) => {
      const next = Math.min(4, Math.max(0.5, Number((current + change).toFixed(2))));
      if (next <= 1) setDeliveryImageOffset({ x: 0, y: 0 });
      return next;
    });
  }

  function handleDeliveryImagePointerDown(event) {
    if (deliveryImageZoom <= 1) return;
    event.preventDefault();
    deliveryImageDragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      originX: deliveryImageOffset.x,
      originY: deliveryImageOffset.y,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
    setDeliveryImageDragging(true);
  }

  function handleDeliveryImagePointerMove(event) {
    const drag = deliveryImageDragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    setDeliveryImageOffset({
      x: drag.originX + event.clientX - drag.startX,
      y: drag.originY + event.clientY - drag.startY,
    });
  }

  function handleDeliveryImagePointerEnd(event) {
    if (deliveryImageDragRef.current?.pointerId !== event.pointerId) return;
    deliveryImageDragRef.current = null;
    setDeliveryImageDragging(false);
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  }

  function showPreviousDeliveryImage() {
    resetDeliveryImageTransform();
    setDeliveryImageViewerIndex((current) => (
      deliveryImageItems.length
        ? (current - 1 + deliveryImageItems.length) % deliveryImageItems.length
        : current
    ));
  }

  function showNextDeliveryImage() {
    resetDeliveryImageTransform();
    setDeliveryImageViewerIndex((current) => (
      deliveryImageItems.length
        ? (current + 1) % deliveryImageItems.length
        : current
    ));
  }

  function chooseDeliveryFiles(event) {
    const selected = Array.from(event.target.files || [])
      .filter((file) => file.size > 0)
      .slice(0, Math.max(0, 6 - deliveryFiles.length))
      .map((file) => ({
        id: crypto.randomUUID(),
        file,
      }));
    setDeliveryFiles((current) => [...current, ...selected]);
    event.target.value = "";
  }

  function removeDeliveryFile(fileId) {
    setDeliveryFiles((current) => current.filter((item) => item.id !== fileId));
  }

  function openClosePanel() {
    setCloseValidationError("");
    setClosePanelOpen(true);
  }

  function submitCloseOrder() {
    const reason = closeForm.reason.trim();
    const refundAmount = closeForm.refundAmount.trim();
    if (!reason) {
      setCloseValidationError("请填写关闭原因");
      return;
    }
    if (requiresRefundAmountOnClose) {
      const refundValue = Number(refundAmount);
      if (!refundAmount || !Number.isFinite(refundValue) || refundValue <= 0) {
        setCloseValidationError("退款金额必须大于零");
        return;
      }
    }
    setCloseValidationError("");
    actionMutation.mutate({ action: "close" });
  }

  return (
    <section className="page-container narrow-page order-detail-page">
      <button className="detail-back" onClick={() => navigate(-1)}><ArrowLeft size={18} /> 返回订单列表</button>
      <div className="page-heading">
        <div><span className="eyebrow">订单详情</span><h1>{order.title}</h1><p>订单号：{order.order_no || order.id}</p></div>
        <div className="order-heading-actions">
          <span className={`status ${String(order.order_status).toLowerCase()}`}><CircleCheck size={15} /> {status}</span>
          {action ? (
            <button
              className={action.key === "close" ? "secondary-button" : "primary-button"}
              disabled={actionMutation.isPending}
              onClick={() => (action.key === "close" ? openClosePanel() : actionMutation.mutate({ action: action.key }))}
            >
              {actionMutation.isPending ? "处理中……" : action.label}
            </button>
          ) : null}
          {secondaryAction ? (
            <button
              className="secondary-button"
              disabled={actionMutation.isPending}
              onClick={() => (secondaryAction.key === "close" ? openClosePanel() : actionMutation.mutate({ action: secondaryAction.key }))}
            >
              {actionMutation.isPending ? "处理中……" : secondaryAction.label}
            </button>
          ) : null}
        </div>
      </div>
      {actionMutation.isError ? <div className="form-error">{getApiErrorMessage(actionMutation.error, "订单操作失败")}</div> : null}
      {order.order_status === "WAITING_PAYMENT" && order.pay_status === "PENDING" ? (
        <div className="form-hint">该订单支付单已创建但尚未完成支付，可点击“继续支付”重试。</div>
      ) : null}
      {closePanelOpen ? (
        <article className="order-close-panel">
          <div className="order-close-header">
            <span className="eyebrow">关闭订单</span>
            <h2>{requiresRefundAmountOnClose ? "关闭并退款" : "确认关闭订单"}</h2>
            <p>
              {requiresRefundAmountOnClose
                ? "当前订单已支付，关闭时需要填写本次退款金额。"
                : "关闭后订单会进入终态，确认前请补充关闭原因。"}
            </p>
          </div>
          <div className="order-close-grid">
            <label className="order-close-field">
              <span>关闭原因</span>
              <textarea
                value={closeForm.reason}
                rows={3}
                maxLength={64}
                onChange={(event) => {
                  setCloseValidationError("");
                  setCloseForm((current) => ({ ...current, reason: event.target.value }));
                }}
                placeholder="例如：项目取消、双方协商终止"
              />
            </label>
            {requiresRefundAmountOnClose ? (
              <label className="order-close-field">
                <span>退款金额</span>
                <div className="money-input">
                  <b>¥</b>
                  <input
                    inputMode="decimal"
                    value={closeForm.refundAmount}
                    onChange={(event) => {
                      setCloseValidationError("");
                      setCloseForm((current) => ({ ...current, refundAmount: event.target.value }));
                    }}
                    placeholder="请输入大于 0 的退款金额"
                  />
                </div>
              </label>
            ) : null}
          </div>
          {closeError ? <div className="form-error">{closeError}</div> : null}
          <div className="order-close-actions">
            <button
              className="secondary-button"
              type="button"
              disabled={actionMutation.isPending}
              onClick={() => {
                setClosePanelOpen(false);
                setCloseValidationError("");
              }}
            >
              取消
            </button>
            <button
              className="primary-button"
              type="button"
              disabled={actionMutation.isPending}
              onClick={submitCloseOrder}
            >
              {actionMutation.isPending ? "处理中……" : (requiresRefundAmountOnClose ? "确认关闭并退款" : "确认关闭订单")}
            </button>
          </div>
        </article>
      ) : null}
      {isProviderPendingConfirm ? (
        <article className="provider-confirm-panel">
          <div>
            <span className="eyebrow">抢单后确认</span>
            <h2>请确认是否承接该订单</h2>
            <p>确认后订单进入服务中；若放弃，平台会释放该订单并重新开放给其他服务者抢单。</p>
          </div>
          <div className="provider-confirm-actions">
            <button
              className="primary-button"
              disabled={actionMutation.isPending}
              onClick={() => actionMutation.mutate({ action: "provider-confirm" })}
            >
              {actionMutation.isPending ? "处理中……" : "确认接单"}
            </button>
            <button
              className="secondary-button"
              disabled={actionMutation.isPending}
              onClick={() => actionMutation.mutate({ action: "provider-reject" })}
            >
              放弃接单
            </button>
          </div>
        </article>
      ) : null}
      {canSubmitDelivery ? (
        <form
          className="delivery-submit-panel"
          onSubmit={(event) => {
            event.preventDefault();
            if (!deliveryMutation.isPending) deliveryMutation.mutate();
          }}
        >
          <div className="delivery-submit-heading">
            <span className="delivery-submit-icon"><FileUp size={20} /></span>
            <div>
              <span className="eyebrow">服务者交付</span>
              <h2>提交本次交付物</h2>
              <p>上传最终文件或阶段成果，提交后订单会进入待验收状态。</p>
            </div>
          </div>
          <div className="delivery-submit-grid">
            <label>
              <span>版本号</span>
              <input
                value={deliveryForm.versionLabel}
                maxLength={32}
                onChange={(event) => setDeliveryForm((current) => ({
                  ...current,
                  versionLabel: event.target.value,
                }))}
                placeholder="例如 v1 / final"
              />
            </label>
            <label>
              <span>交付说明</span>
              <textarea
                value={deliveryForm.summary}
                rows={4}
                onChange={(event) => setDeliveryForm((current) => ({
                  ...current,
                  summary: event.target.value,
                }))}
                placeholder="说明本次交付内容、文件结构、注意事项或验收建议"
              />
            </label>
          </div>
          <label className="delivery-file-upload">
            <FileUp size={18} />
            <span>
              <strong>上传交付文件</strong>
              <small>最多 6 个文件，可上传图片、压缩包、源文件或说明文档。</small>
            </span>
            <input type="file" multiple onChange={chooseDeliveryFiles} />
          </label>
          {deliveryFiles.length ? (
            <div className="delivery-file-list">
              {deliveryFiles.map((item) => (
                <span key={item.id}>
                  {item.file.name}
                  <button type="button" aria-label={`移除 ${item.file.name}`} onClick={() => removeDeliveryFile(item.id)}>
                    <X size={14} />
                  </button>
                </span>
              ))}
            </div>
          ) : null}
          {deliveryMutation.isPending ? (
            <div className="order-upload-progress delivery-progress">
              <span style={{ width: `${deliveryProgress}%` }} />
              <b>{deliveryProgress < 100 ? `正在处理交付文件 ${deliveryProgress}%` : "正在提交交付……"}</b>
            </div>
          ) : null}
          {deliveryError ? <div className="form-error">{deliveryError}</div> : null}
          <div className="delivery-submit-actions">
            <p>提交后需求方可以验收；如需求方要求修改，订单会回到修改中。</p>
            <button className="primary-button" type="submit" disabled={deliveryMutation.isPending}>
              {deliveryMutation.isPending ? "提交中……" : "提交交付"}
            </button>
          </div>
        </form>
      ) : null}
      {["DELIVERED", "COMPLETED", "IN_REVISION", "IN_DISPUTE"].includes(order.order_status) ? (
        <article className="delivery-review-panel">
          <div className="delivery-submit-heading">
            <span className="delivery-submit-icon"><Download size={20} /></span>
            <div>
              <span className="eyebrow">交付内容</span>
              <h2>{latestDelivery ? `版本 ${latestDelivery.versionLabel}` : "等待交付明细"}</h2>
              <p>
                {deliveriesQuery.isPending
                  ? "正在加载服务者提交的交付内容……"
                  : deliveriesMethodNotAllowed
                    ? "当前后端实例尚未启用交付查询接口，已降级显示可用交付信息。"
                  : latestDelivery
                  ? `提交时间：${formatDetailDate(latestDelivery.submittedAt) || "待确认"}`
                    : "当前订单已进入验收阶段，但暂未查询到交付文件明细。"}
              </p>
            </div>
          </div>
          {deliveriesQuery.isError && !deliveriesMethodNotAllowed ? (
            <div className="form-error">{getApiErrorMessage(deliveriesQuery.error, "交付内容加载失败")}</div>
          ) : null}
          {deliveriesMethodNotAllowed ? (
            <div className="form-hint">交付内容查询 GET 当前返回 405，请确认后端服务已重启并部署包含 GET /quick-orders/&lt;order_id&gt;/deliveries/ 的版本。</div>
          ) : null}
          {latestDelivery?.summary ? (
            <p className="delivery-review-summary">{latestDelivery.summary}</p>
          ) : null}
          {settlementSummary || acceptanceSnapshot?.settlementId || acceptanceSnapshot?.settlementStatus ? (
            <article className="settlement-summary-panel">
              <div className="settlement-summary-header">
                <span className="eyebrow">结算进度</span>
                <h3>{getSettlementStatusCopy(settlementSummary?.status || acceptanceSnapshot?.settlementStatus)}</h3>
                <p>{getSettlementDescription(settlementSummary, acceptanceSnapshot)}</p>
              </div>
              {settlementSummary ? (
                <div className="settlement-summary-grid">
                  <div><dt>结算单号</dt><dd>{settlementSummary.settlementNo || settlementSummary.id || "待同步"}</dd></div>
                  <div><dt>服务者到账</dt><dd>¥{formatMoney(settlementSummary.providerAmount)}</dd></div>
                  <div><dt>订单原金额</dt><dd>¥{formatMoney(settlementSummary.grossAmount)}</dd></div>
                  <div><dt>平台服务费</dt><dd>¥{formatMoney(settlementSummary.platformFeeAmount)}</dd></div>
                  <div><dt>退款冲减</dt><dd>¥{formatMoney(settlementSummary.refundAdjustmentAmount)}</dd></div>
                  <div><dt>打款渠道</dt><dd>{settlementSummary.channel || "待同步"}</dd></div>
                  <div><dt>收款账户</dt><dd>{settlementSummary.accountMasked || "待同步"}</dd></div>
                  <div><dt>到账时间</dt><dd>{formatDetailDate(settlementSummary.settledAt) || formatDetailDate(settlementSummary.availableAt) || "处理中"}</dd></div>
                </div>
              ) : (
                <div className="form-hint">
                  结算单已创建：{acceptanceSnapshot?.settlementId || "待同步"}，详情正在回传中。
                </div>
              )}
              {settlementSummary?.failureCode ? (
                <div className="form-hint">失败原因：{settlementSummary.failureCode}</div>
              ) : null}
            </article>
          ) : null}
          {canReviewDelivery ? (
            <div className="delivery-acceptance-panel">
              <div className="delivery-acceptance-header">
                <strong>验收操作</strong>
                <p>
                  交付符合要求时可直接通过验收；如仍需调整，可退回修改。
                  当前剩余修改次数：{order.remaining_revision_rounds ?? 0} 次。
                </p>
              </div>
              <label className="delivery-review-field">
                <span>验收备注</span>
                <textarea
                  value={reviewForm.acceptanceNote}
                  rows={3}
                  onChange={(event) => setReviewForm((current) => ({
                    ...current,
                    acceptanceNote: event.target.value,
                  }))}
                  placeholder="可选填写验收结论、交付亮点或后续协作备注"
                />
              </label>
              <div className="delivery-revision-panel">
                <div className="delivery-revision-copy">
                  <strong>退回修改</strong>
                  <p>请明确指出需要调整的内容，方便服务者按轮次重新交付。</p>
                </div>
                <label className="delivery-review-field">
                  <span>修改原因</span>
                  <textarea
                    value={reviewForm.revisionReason}
                    rows={3}
                    onChange={(event) => setReviewForm((current) => ({
                      ...current,
                      revisionReason: event.target.value,
                    }))}
                    placeholder="例如：主KV风格已对齐，但副标题信息层级不够清晰"
                  />
                </label>
                <label className="delivery-review-field">
                  <span>修改项清单</span>
                  <textarea
                    value={reviewForm.revisionItems}
                    rows={4}
                    onChange={(event) => setReviewForm((current) => ({
                      ...current,
                      revisionItems: event.target.value,
                    }))}
                    placeholder={"每行填写一个修改项\n例如：补一版深色背景封面\n导出 1080x1920 竖版成图"}
                  />
                </label>
                {!canRequestRevision ? (
                  <div className="form-hint">当前订单已没有剩余修改次数，如交付仍有争议，建议走争议流程。</div>
                ) : null}
              </div>
              <div className="delivery-review-footer-actions">
              {counterparty ? (
                  <button
                    className="secondary-button"
                    type="button"
                    disabled={!counterparty.userId}
                    onClick={() => navigate("/messages", { state: { targetUserId: counterparty.userId } })}
                  >
                    <MessageCircle size={17} />
                    {counterparty.buttonLabel}
                  </button>
                ) : null}
                {canRequestRevision ? (
                  <button
                    className="secondary-button"
                    type="button"
                    disabled={reviewMutation.isPending}
                    onClick={() => reviewMutation.mutate({ action: "revision" })}
                  >
                    {reviewMutation.isPending ? "处理中……" : "退回修改"}
                  </button>
                ) : null}
                <button
                  className="primary-button"
                  type="button"
                  disabled={reviewMutation.isPending}
                  onClick={() => reviewMutation.mutate({ action: "accept" })}
                >
                  {reviewMutation.isPending ? "处理中……" : "通过验收"}
                </button>
              </div>
              {reviewError ? <div className="form-error">{reviewError}</div> : null}
            </div>
          ) : null}
          {isDemandUser && order.order_status === "IN_REVISION" ? (
            <div className="form-hint">你已发起修改请求，当前等待服务者根据本轮意见重新交付。</div>
          ) : null}
          {latestDelivery?.files?.length ? (
            <div className="delivery-review-files">
              {latestDelivery.files.map((file) => (
                <DeliveryFilePreview
                  key={file.id}
                  file={file}
                  onOpenImage={openDeliveryImageViewer}
                />
              ))}
            </div>
          ) : (
            <div className="delivery-review-empty">
              <strong>暂无可展示的交付文件</strong>
              <p>{deliveriesMethodNotAllowed ? "当前运行中的后端实例未开放交付查询 GET，暂无法读取服务者已提交的交付附件。" : "交付列表接口当前没有返回文件明细；可刷新页面或联系服务者确认是否已上传交付附件。"}</p>
            </div>
          )}
          {latestDelivery?.isLocalOnly ? (
            <div className="form-hint">这是本机提交交付后缓存的展示快照；跨账号验收仍需要后端订单详情接口返回交付明细。</div>
          ) : null}
        </article>
      ) : null}
      <div className="order-detail-grid">
        <article className="form-card order-detail-card">
          <div className="detail-card-heading"><h2>需求信息</h2><Clock3 size={18} /></div>
          <dl>
            <div><dt>预算金额</dt><dd>¥{order.amount_yuan || "0.00"}</dd></div>
            <div><dt>需求描述</dt><dd>{order.requirement_desc || "暂无描述"}</dd></div>
            <div><dt>交付说明</dt><dd>{order.deliverable_desc || "以双方确认内容为准"}</dd></div>
            <div><dt>验收标准</dt><dd>{order.acceptance_criteria || "暂无验收标准"}</dd></div>
          </dl>
        </article>
        <article className="form-card order-detail-card">
          <div className="detail-card-heading"><h2>进度状态</h2></div>
          <div className="order-progress">
            <strong>{status}</strong>
            <span>支付状态：{order.pay_status || "待确认"}</span>
            <span>修改轮次：{order.remaining_revision_rounds ?? 0} 次剩余</span>
            <span>
              沟通入口：
              {counterparty?.userId
                ? `可直接联系${counterparty.roleLabel}`
                : counterparty
                  ? "服务者接单后开放"
                  : "当前账号暂无法识别订单角色"}
            </span>
          </div>
        </article>
      </div>
      <DeliveryImageViewer
        items={deliveryImageItems}
        activeIndex={deliveryImageViewerIndex}
        zoom={deliveryImageZoom}
        offset={deliveryImageOffset}
        dragging={deliveryImageDragging}
        onClose={() => setDeliveryImageViewerIndex(-1)}
        onPrevious={showPreviousDeliveryImage}
        onNext={showNextDeliveryImage}
        onZoom={changeDeliveryImageZoom}
        onReset={resetDeliveryImageTransform}
        onPointerDown={handleDeliveryImagePointerDown}
        onPointerMove={handleDeliveryImagePointerMove}
        onPointerEnd={handleDeliveryImagePointerEnd}
      />
    </section>
  );
}
